import axios from 'axios';
import { readReportIdFromSession } from './fetchExistingReportId';

const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;
const BASE_URL = process.env.REACT_APP_REPORTS_BASE_URL;

const uuid4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

/** Pull report id from game-server responses (shape varies by endpoint). */
export function extractReportIdFromResponse(payload) {
  if (payload == null || typeof payload !== 'object') return null;
  const candidates = [
    payload.id,
    payload.reportId,
    payload.report_id,
    payload.reportid,
    payload.data?.id,
    payload.data?.reportId,
    payload.data?.report_id,
    payload.data?.reportid,
    payload.data?.data?.id,
    payload.data?.data?.reportId,
    payload.data?.data?.report_id,
    payload.result?.id,
    payload.result?.reportId,
    payload.result?.report_id,
    payload.result?.data?.id,
    payload.result?.data?.reportId,
    payload.result?.data?.report_id,
    payload.report?.id,
    payload.report?.reportId,
    payload.report?.report_id,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return null;
}


export async function refreshToken(currentToken) {
  const url = `${BASE_URL}/auth/rf-token`;
  const headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    ...(currentToken && { Authorization: `Bearer ${currentToken}` }),
    'Idempotency-Key': uuid4(),
  };
  const response = await axios.get(url, { headers });
  return response.data.token;
}

export async function sendReport(data) {
  const { sessionId, role, token } = data;
  if (String(role ?? '').toLowerCase() === 'demobypass') return true;

  const persistReportIdToSession = (payload) => {
    try {
      const rid = extractReportIdFromResponse(payload);
      if (!rid) return;
      const raw = sessionStorage.getItem("userData") || "{}";
      const ud = JSON.parse(raw) || {};
      if (ud.reportId && String(ud.reportId).trim() !== "") return;
      sessionStorage.setItem("userData", JSON.stringify({ ...ud, reportId: String(rid).trim() }));
    } catch {
      // ignore
    }
  };

  const isGuest = String(role ?? '').toUpperCase() === 'GUEST_USER';
  if (isGuest) {
    const endpoint = data.reportId ? 'game/update/guest/score' : 'game/add/guest/score';
    const guestPayload = data.reportId
      ? { points: data.points, time: data.time, reportId: data.reportId }
      : {
          points: data.points,
          time: data.time,
          gameId: data.gameId,
          name: data.name,
          sessionId: data.sessionId,
          userId: data.userId,
          organizationId: data.organizationId,
          ans: "",
        };
    const url = `${BASE_URL}/${endpoint}`;
    const method = data.reportId ? 'put' : 'post';
    const headers = {
      'client-secret': CLIENT_SECRET,
      'Content-Type': 'application/json',
      'Idempotency-Key': uuid4(),
    };
    const response = await axios({ method, url, headers, data: guestPayload });
    persistReportIdToSession(response.data);
    return response.data;
  }

  // Registered user logic: keep exactly one report row per user/session.
  // If reportId is missing but the backend already issued one earlier in the session,
  // reuse it so we don't create duplicates.
  let reportIdToUse = data.reportId;
  if (!reportIdToUse) {
    try {
      const cached = JSON.parse(sessionStorage.getItem("userData") || "{}");
      reportIdToUse = readReportIdFromSession(cached);
    } catch {
      // ignore
    }
  }

  const isUpdate = Boolean(reportIdToUse);
  const url = isUpdate
    ? `${BASE_URL}/game-server/report/${reportIdToUse}`
    : `${BASE_URL}/game-server/report/add`;
  const method = isUpdate ? 'put' : 'post';

  let headers = {
    'client-secret': CLIENT_SECRET,
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'Idempotency-Key': uuid4(),
  };

  try {
    const response = await axios({ method, url, headers, data });
    persistReportIdToSession(response.data);
    return response.data;
  } catch (err) {
    if (err.response?.status === 401) {
      const newToken = await refreshToken(token);
      headers.Authorization = `Bearer ${newToken}`;
      const retry = await axios({ method, url, headers, data });
      persistReportIdToSession(retry.data);
      return retry.data;
    }
    throw err;
  }
}